/**
 * Operational Summary Resolver — Phase 2a
 * ───────────────────────────────────────
 * Pure aggregator over existing AppDataContext arrays. Returns daily +
 * weekly snapshot counts only. No per-actor breakdown (PM identity
 * audit blocks that). No new telemetry.
 */

import type { Job, Contractor } from "@/data/types";
import type { Callback } from "@/data/contractor/networkExecution/callback";
import type { HoldbackLedgerEntry } from "@/data/contractor/networkExecution/holdback/ledger";
import type { DisputeEvent } from "@/data/contractor/networkExecution/dispute/dispute";
import type { SoftEnforcementSuggestion } from "@/data/contractor/networkExecution/softEnforcement/suggestions";
import type { OverrideOutcomeRecord } from "@/data/contractor/networkExecution/overrideRereview";

const COMPLETED_STAGES = new Set(["Completed", "QA Review"]);
const DAY_MS = 24 * 60 * 60 * 1000;

export interface OperationalSummary {
  // Live state
  activeJobs: number;
  probationaryContractors: number;
  // 24h windows
  callbacks24h: number;
  disputesOpened24h: number;
  holdbacksCreated24h: number;
  holdbacksReleased24h: number;
  // 7d windows
  callbacks7d: number;
  disputesOpened7d: number;
  holdbacksCreated7d: number;
  // Outstanding advisories
  pendingSuggestions: number;
  expiredSuggestions: number;
  // Override pipeline
  overridesPending: number;
  overridesNegative7d: number;
}

export interface BuildSummaryInput {
  jobs: ReadonlyArray<Job>;
  contractors: ReadonlyArray<Contractor>;
  callbacks: ReadonlyArray<Callback>;
  holdbackLedger: ReadonlyArray<HoldbackLedgerEntry>;
  disputeEvents: ReadonlyArray<DisputeEvent>;
  softSuggestions: ReadonlyArray<SoftEnforcementSuggestion>;
  overrideOutcomes: ReadonlyArray<OverrideOutcomeRecord>;
  /** Reference time for window math. Defaults to Date.now(). */
  nowMs?: number;
}

export function buildOperationalSummary(input: BuildSummaryInput): OperationalSummary {
  const now = input.nowMs ?? Date.now();
  const cutoff24 = now - DAY_MS;
  const cutoff7 = now - 7 * DAY_MS;

  const within = (iso: string, cutoff: number) => {
    const t = new Date(iso).getTime();
    return Number.isFinite(t) && t >= cutoff;
  };

  const callbacks24h = input.callbacks.filter((c) => within(c.filedAt, cutoff24)).length;
  const callbacks7d = input.callbacks.filter((c) => within(c.filedAt, cutoff7)).length;

  const disputesOpened24h = input.disputeEvents.filter(
    (d) => d.type === "open" && within(d.createdAt, cutoff24),
  ).length;
  const disputesOpened7d = input.disputeEvents.filter(
    (d) => d.type === "open" && within(d.createdAt, cutoff7),
  ).length;

  const holdbacksCreated24h = input.holdbackLedger.filter(
    (h) => h.action === "create" && within(h.createdAt, cutoff24),
  ).length;
  const holdbacksReleased24h = input.holdbackLedger.filter(
    (h) => h.action === "release" && within(h.createdAt, cutoff24),
  ).length;
  const holdbacksCreated7d = input.holdbackLedger.filter(
    (h) => h.action === "create" && within(h.createdAt, cutoff7),
  ).length;

  return {
    activeJobs: input.jobs.filter((j) => !COMPLETED_STAGES.has(j.currentStage)).length,
    probationaryContractors: input.contractors.filter((c) => (c as { isOnProbation?: boolean }).isOnProbation === true).length,
    callbacks24h,
    disputesOpened24h,
    holdbacksCreated24h,
    holdbacksReleased24h,
    callbacks7d,
    disputesOpened7d,
    holdbacksCreated7d,
    pendingSuggestions: input.softSuggestions.filter((s) => s.outcome === "pending").length,
    expiredSuggestions: input.softSuggestions.filter((s) => s.outcome === "ignored_expired").length,
    overridesPending: input.overrideOutcomes.filter((o) => o.outcome === "pending").length,
    overridesNegative7d: input.overrideOutcomes.filter(
      (o) => o.outcome === "negative" && within(o.recordedAt, cutoff7),
    ).length,
  };
}
